import { Habit, HabitFrequency } from '../types';

export interface HabitValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Checks if a habit is scheduled for a specific date based on its frequency configuration
 */
export function isHabitScheduledForDate(habit: Habit, date: Date = new Date()): boolean {
  switch (habit.frequency) {
    case HabitFrequency.Daily:
      return true;
    case HabitFrequency.WeeklySpecificDays:
      return isScheduledForWeeklySpecificDays(habit, date);
    case HabitFrequency.WeeklyCount:
      return true; // WeeklyCount allows completion on any day
    case HabitFrequency.IntervalBased:
      return isScheduledForIntervalBased(habit, date);
    default:
      return true;
  }
}

/**
 * Validates if a habit can be completed on a specific date and returns validation result with message
 */
export function validateHabitCompletion(habit: Habit, date: Date = new Date()): HabitValidationResult {
  const isValid = isHabitScheduledForDate(habit, date);
  
  if (isValid) {
    return { isValid: true };
  }

  return {
    isValid: false,
    message: getScheduleValidationMessage(habit)
  };
}

/**
 * Checks if a habit with WeeklySpecificDays frequency is scheduled for the given date
 */
function isScheduledForWeeklySpecificDays(habit: Habit, date: Date): boolean {
  // Check if this is actually a WeeklySpecificDays habit
  if (habit.frequency !== HabitFrequency.WeeklySpecificDays) {
    return false;
  }
  
  if (!habit.weeklyDays || habit.weeklyDays.length === 0) {
    return false;
  }

  // Get day of week (0=Sunday, 1=Monday, etc.)
  const dayOfWeek = date.getDay();
  return habit.weeklyDays.includes(dayOfWeek);
}

/**
 * Checks if a habit with IntervalBased frequency is scheduled for the given date
 */
function isScheduledForIntervalBased(habit: Habit, date: Date): boolean {
  // Check if this is actually an IntervalBased habit
  if (habit.frequency !== HabitFrequency.IntervalBased) {
    return false;
  }
  
  if (!habit.intervalDays || habit.intervalDays <= 0) {
    return false;
  }

  // For interval-based habits, check if the date aligns with the interval
  // This is a simplified implementation - matches backend logic
  const createdAt = new Date(habit.createdAt);
  const daysSinceCreation = Math.floor((date.getTime() - createdAt.getTime()) / (24 * 60 * 60 * 1000));
  return daysSinceCreation % habit.intervalDays === 0;
}

/**
 * Gets the appropriate validation message for a habit that's not scheduled for the current date
 */
function getScheduleValidationMessage(habit: Habit): string {
  switch (habit.frequency) {
    case HabitFrequency.WeeklySpecificDays:
      return getWeeklySpecificDaysMessage(habit);
    case HabitFrequency.IntervalBased:
      return `This habit is scheduled every ${habit.intervalDays} days`;
    default:
      return 'This habit is not scheduled for today';
  }
}

/**
 * Gets a user-friendly message for WeeklySpecificDays habits
 */
function getWeeklySpecificDaysMessage(habit: Habit): string {
  if (!habit.weeklyDays || habit.weeklyDays.length === 0) {
    return 'This habit has no scheduled days';
  }

  const dayNames = habit.weeklyDays.map(getDayName);
  const daysText = dayNames.length === 1 
    ? dayNames[0] 
    : dayNames.slice(0, -1).join(', ') + ' and ' + dayNames[dayNames.length - 1];
  
  return `This habit is only scheduled for ${daysText}`;
}

/**
 * Converts day index to day name
 */
function getDayName(dayIndex: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayIndex] || 'Unknown';
}

/**
 * Checks if today is a scheduled day for the habit
 */
export function isHabitScheduledForToday(habit: Habit): boolean {
  return isHabitScheduledForDate(habit, new Date());
}

/**
 * Gets validation result for completing a habit today
 */
export function validateHabitCompletionForToday(habit: Habit): HabitValidationResult {
  return validateHabitCompletion(habit, new Date());
} 